/**
 * Sign Up
 */
import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import LinearProgress from '@material-ui/core/LinearProgress';
import Toolbar from '@material-ui/core/Toolbar';
import EmailIcon from '@material-ui/icons/Email';
import LockIcon from '@material-ui/icons/Lock';
import PersonIcon from '@material-ui/icons/Person';
import QueueAnim from 'rc-queue-anim';
import React, {useRef} from 'react';
import {connect} from 'react-redux';
import {Link} from 'react-router-dom';
import {compose} from 'redux';
import {Field} from 'redux-form';
import {signupUser} from '../../actions';
import CustomTextField from '../../components/Input/CustomTextField';
import AppConfig from '../../config/AppConfig';
import {
    endpointGetExternalServiceAuthUrl,
    externalServiceProviderFacebook,
    externalServiceProviderGoogle,
    resourceCustomUsers,
    siteUrlPrivacy,
    siteUrlTerms
} from '../../constants';
import {SimpleForm} from '../../controlComponents';
import IntlMessage from '../../util/IntlMessage.tsx';
import LoginFormToolbar from './LoginFormToolbar';
import SessionSlider from './SessionSlider';
import EmailVerificationDialog from './EmailVerificationDialog';
import {Fab} from "@material-ui/core";
import {FETCH} from "../../fetchApi/types";
import {NotificationManager} from "react-notifications";
import {withDataProvider} from "../../fetchApi/dataProvider";
import ReCAPTCHA from 'react-google-recaptcha';

const formValidate = (fields) => {
    const errors = {};
    const fieldsToValidate = ['username', 'password', 'email'];
    fieldsToValidate.forEach((fieldName) => {
        if (!fields[fieldName]) {
            errors[fieldName] = 'Field is required';
        }
    });
    if (!!fields.email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,6}$/i.test(fields.email)) {
        errors.email = 'Email is invalid';
    }
    return errors;
};

const getAuthorizeUrlForExternalService = async (provider, dataProvider) => {
    dataProvider(FETCH, `${resourceCustomUsers}/${endpointGetExternalServiceAuthUrl}`, {provider})
        .then((data) => {
            window.location.assign(data.data.url);
        })
        .catch((e) => {
            NotificationManager.error(e.message);
        });
};

const SignUp = ({signupUser, history, loading, dataProvider, ...restProps}) => {
    const recaptchaRef = useRef(null);

    const onUserSignUp = async (values) => {
        const {email, password, username} = values;
        const captchaToken = await recaptchaRef.current.executeAsync();
        recaptchaRef.current.reset();

        signupUser({email, password, username, captchaToken}, history);
    };

    return (
        <QueueAnim type="bottom" duration={2000}>
            <div className="rct-session-wrapper">
                {loading && <LinearProgress />}
                <AppBar position="static" className="session-header">
                    <Toolbar>
                        <div className="container">
                            <div className="d-flex justify-content-between">
                                <div className="session-logo"/>
                                <div>
                                    <Link to="/signin" className="mr-15 text-white">
                                        Already have an account?
                                    </Link>
                                    <Button component={Link} to="/signin" variant="contained" className="btn-light">
                                        Sign In
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Toolbar>
                </AppBar>
                <div className="session-inner-wrapper">
                    <div className="container">
                        <div className="row row-eq-height">
                            <div className="col-sm-7 col-md-7 col-lg-8">
                                <div className="session-body text-center">
                                    <div className="session-head mb-15">
                                        <h2>Get started with {AppConfig.brandName}</h2>
                                        <p className="mb-0">Most powerful document management platform</p>
                                    </div>
                                    <SimpleForm
                                        {...restProps}
                                        form="sign-up-form"
                                        save={onUserSignUp}
                                        toolbar={<LoginFormToolbar buttonLabel="actions.sign_up" />}
                                        validate={formValidate}
                                    >
                                        <Grid
                                            container
                                            justify="space-between"
                                            spacing={0}
                                            alignContent="flex-start"
                                            className="edit-form-grid"
                                        >
                                            <Grid item md={12}>
                                                <Field
                                                    type="mail"
                                                    name="email"
                                                    id="user-mail"
                                                    label={<IntlMessage id="login.fields.email" />}
                                                    component={CustomTextField}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <EmailIcon />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    variant="outlined"
                                                    autoFocus
                                                />
                                            </Grid>
                                            <Grid item md={12}>
                                                <Field
                                                    type="password"
                                                    name="password"
                                                    label={<IntlMessage id="login.fields.password" />}
                                                    component={CustomTextField}
                                                    variant="outlined"
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <LockIcon />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item md={12}>
                                                <Field
                                                    type="text"
                                                    name="username"
                                                    label={<IntlMessage id="login.fields.username" />}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <PersonIcon />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    variant="outlined"
                                                    component={CustomTextField}
                                                    id="user-name"
                                                />
                                            </Grid>
                                        </Grid>
                                        <ReCAPTCHA
                                            ref={recaptchaRef}
                                            sitekey={'6Lc5m9IgAAAAALJ4zRgTNnapzm7MO67hHl_9HTZs'}
                                            size='invisible'
                                        />
                                    </SimpleForm>
                                    <p className='sign-in-links'>
                                        <Link to="/signin">
                                            Already have an account?
                                        </Link>
                                    </p>
                                    <p className="mb-20">or sign up with</p>
                                    <Fab
                                        variant="round"
                                        size="small"
                                        className="btn-facebook ml-10 mr-10 mb-20 text-white"
                                        onClick={() => {
                                            getAuthorizeUrlForExternalService(
                                                externalServiceProviderFacebook, dataProvider);
                                        }}
                                    >
                                        <i className="zmdi zmdi-facebook" />
                                    </Fab>
                                    <Fab
                                        variant="round"
                                        size="small"
                                        className="btn-google ml-10 mr-10 mb-20 text-white"
                                        onClick={() => {
                                            getAuthorizeUrlForExternalService(
                                                externalServiceProviderGoogle, dataProvider);
                                        }}
                                    >
                                        <i className="zmdi zmdi-google" />
                                    </Fab>
                                    <p className="text-muted">By signing up you agree to {AppConfig.brandName}</p>
                                    <p className="mb-0 text-muted">
                                        <a target="_blank" href={siteUrlTerms} className="text-muted" rel="noreferrer">
                                            Terms of Service
                                        </a> and <a target="_blank" href={siteUrlPrivacy} className="text-muted" rel="noreferrer">
                                            Privacy policy
                                        </a>
                                    </p>
                                </div>
                            </div>
                            <div className="col-sm-5 col-md-5 col-lg-4">
                                <SessionSlider />
                            </div>
                        </div>
                    </div>
                    <EmailVerificationDialog />
                </div>
            </div>
        </QueueAnim>
    );
};

// map state to props
const mapStateToProps = ({authUser}) => {
    const {loading} = authUser;
    return {loading};
};

const enhance = compose(
    connect(mapStateToProps, {
        signupUser,
    }),
    withDataProvider
);

const EnhancedSignUp = enhance(SignUp);

export default EnhancedSignUp;
